import { typographyPlugin } from './utils/tailwind/plugins/typography'
import type { Config } from 'tailwindcss'

const customDimensions = {
  13: '3.25rem',
  18: '4.5rem',
  22: '5.5rem',
  30: '7.5rem',
  34: '8.5rem',
  38: '9.5rem',
  42: '10.5rem',
  46: '11.5rem',
  50: '12.5rem',
  52: '13rem',
  54: '13.5rem',
  60: '15rem',
  62: '15.5rem',
  64: '16rem',
  68: '17rem',
  70: '17.5rem',
  74: '18.5rem',
  75: '18.75rem',
  81: '20.25rem',
  84: '21rem',
  85: '21.25rem',
  90: '22.5rem',
  100: '25rem',
  104: '26rem',
  120: '30rem',
  130: '32.5rem',
  138: '34.5rem',
  140: '35rem',
  150: '37.5rem',
  160: '40rem',
  166: '41.5rem',
  170: '42.5rem',
  174: '43.5rem',
  176: '44rem',
  186: '46.5rem',
  200: '50rem',
  244: '61rem',
  330: '82.5rem',
  400: '100rem'
}

const customSpacings = {
  18: '4.5rem',
  22: '5.5rem',
  354: '88.5rem'
}

export const SCREEN_SM = 768
export const SCREEN_MD = 1024
export const SCREEN_LG = 1200
export const SCREEN_XL = 1440

const SCREENS = {
  sm: `${SCREEN_SM}px`,
  md: `${SCREEN_MD}px`,
  lg: `${SCREEN_LG}px`,
  xl: `${SCREEN_XL}px`
}

export default <Config>{
  content: [
    './components/**/*.{vue,js,ts}',
    './layouts/**/*.vue',
    './pages/**/*.vue',
    './composables/**/*.{vue,js,ts}',
    './widgets/**/*.vue',
    './storyblok/**/*.{vue,js,ts}',
    './emails/**/*.{vue,js,ts}',
    './app.vue'
  ],
  plugins: [typographyPlugin],
  theme: {
    container: {
      center: true,
      padding: {
        DEFAULT: '1.25rem',
        sm: '1.25rem',
        md: '1.25rem',
        lg: '0',
        xl: '0'
      },
      screens: {
        sm: '100%',
        md: '100%',
        lg: '1128px',
        xl: '1416px'
      }
    },
    screens: SCREENS,
    fontFamily: {
      sans: ['Roboto', 'sans-serif']
    },
    fontSize: {
      '2xs': '0.5rem',
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      md: '1.125rem',
      lg: '1.25rem',
      xl: '1.5rem',
      '2xl': '1.75rem',
      '3xl': '2rem',
      '4xl': '2.5rem',
      '5xl': '3rem',
      '6xl': '3.5rem',
      '7xl': '4rem'
    },
    boxShadow: {
      none: '0 0 #0000',
      xs: '0 1px 4px 0 rgba(192,200,216,.3)',
      sm: '0 4px 8px 0 rgba(21, 38, 73, 0.20)',
      md: '0 8px 32px 0 rgba(21, 38, 73, 0.16)',
      lg: '0 8px 48px 0 rgba(21, 38, 73, 0.08)',
      sidebar: '0 0 8px 0 rgba(21, 38, 73, 0.20)',
      green: '4px 0 0 0 rgba(205, 218, 50, 1) inset',
      slider: '0 4px 32px 0 rgba(21, 38, 73, 0.08)'
    },
    borderRadius: {
      DEFAULT: '0.25rem',
      none: '0',
      xs: '0.25rem',
      sm: '0.5rem',
      md: '1rem',
      lg: '2.5rem',
      full: '9999px'
    },
    extend: {
      colors: {
        secondary: '#69768F',
        bt: {
          green: {
            DEFAULT: '#CDDA32',
            active: '#ACB90C',
            hover: '#D9EC00',
            light: '#CDDA320D',
            dull: '#F4F8DC'
          },
          'light-gray': '#FAFAF8',
          blue: {
            dark: '#152649',
            ui: '#204588',
            'ui-light': '#2754A5'
          },
          red: {
            DEFAULT: '#CD5A5B',
            dark: '#9d0025'
          },
          grabout: {
            violet: {
              DEFAULT: '#8121BA'
            }
          }
        },
        link: '#313892',
        divider: '#DADEE7',
        border: '#C0C8D8',
        grey: {
          DEFAULT: '#F7F7F7',
          light: '#F8F9FF',
          select: '#E8EBF0',
          subtitle: '#737D92',
          dark: '#4C5367'
        },
        light: {
          error: '#FBF1F1',
          warning: '#FFF5E4',
          success: '#EBFFE9',
          info: '#EFF8FF'
        },
        dark: {
          error: '#BC3C3C',
          warning: '#EF8D34',
          success: '#46BC3C',
          info: '#204588'
        },
        focus: '#009EF0',
        dullIcon: '#999',
        input: {
          grey: '#EBEEF5'
        },
        calendar: {
          day: {
            selected: '#f0f4fb',
            outline: '#e2e3e8'
          }
        }
      },
      lineHeight: {
        11: '2.75rem',
        14: '3.5rem',
        15: '3.75rem',
        16: '4rem',
        17: '4.5rem',
        18: '5rem'
      },
      keyframes: {
        'accordion-down': {
          from: { height: '0' },
          to: { height: 'var(--radix-accordion-content-height)' }
        },
        'accordion-up': {
          from: { height: 'var(--radix-accordion-content-height)' },
          to: { height: '0' }
        },
        'slide-up': {
          from: { height: 'var(--radix-collapsible-content-height)' },
          to: { height: '0' }
        },
        'slide-down': {
          from: { height: '0' },
          to: { height: 'var(--radix-collapsible-content-height)' }
        }
      },
      animation: {
        'accordion-down': 'accordion-down 0.3s ease-out',
        'accordion-up': 'accordion-up 0.3s ease-out',
        'slide-down': 'slide-down 0.3s ease-out',
        'slide-up': 'slide-up 0.3s ease-out'
      },
      padding: {
        ...customDimensions
      },
      width: {
        ...customDimensions
      },
      maxWidth: {
        ...customDimensions
      },
      minWidth: {
        ...customDimensions
      },
      height: {
        ...customDimensions
      },
      maxHeight: {
        ...customDimensions
      },
      minHeight: {
        ...customDimensions
      },
      size: {
        ...customDimensions
      },
      gap: {
        ...customDimensions
      },
      margin: {
        ...customSpacings
      },
      zIndex: {
        '5': '5'
      },
      gridAutoRows: {
        '1': '1fr'
      }
    }
  }
}
